import invariant from 'tiny-invariant'
import { useLocaleContext } from 'fbtee'
import { TabsList, TabsTrigger } from './ui/tabs'
import { Skeleton } from './ui/skeleton'
import { useForceLoading } from '../lib/skeletonDebug'
import { formatDayShort } from '../lib/formatDate'
import { cn } from '../lib/utils'

const SKELETON_TABS = [0, 1, 2]

export function DayTabsList({
  loading: loadingProp,
  days,
  formatLabel,
  className,
  bleed,
}: {
  loading?: boolean
  days?: string[]
  formatLabel?: (day: string) => React.ReactNode
  className?: string
  bleed?: boolean
}) {
  const { locale } = useLocaleContext()
  const loading = useForceLoading(loadingProp)

  if (loading) {
    return (
      <div className={`inline-flex w-full h-8 items-center rounded-lg bg-muted p-[3px] gap-1 ${className ?? ''}`}>
        {SKELETON_TABS.map(i => (
          <div key={i} className="flex-1 flex items-center justify-center">
            <Skeleton className="h-4 w-12" />
          </div>
        ))}
      </div>
    )
  }

  invariant(days, 'DayTabsList: days is required when not loading')

  const getLabel = formatLabel ?? ((day: string) => formatDayShort(day, locale))

  if (days.length > 3) {
    return (
      <div
        data-no-swipe
        className={cn(
          'overflow-x-auto overflow-y-hidden [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
          bleed && '-mx-4',
        )}
      >
        <TabsList className={cn('w-max', bleed && 'mx-4', className)}>
          {days.map(day => (
            <TabsTrigger key={day} value={day} className="flex-none min-w-[calc((100vw-2.5rem)/3.4)] md:flex-1 md:min-w-0">
              {getLabel(day)}
            </TabsTrigger>
          ))}
        </TabsList>
      </div>
    )
  }

  return (
    <TabsList className={`w-full ${className ?? ''}`}>
      {days.map(day => (
        <TabsTrigger key={day} value={day} className="flex-1">
          {getLabel(day)}
        </TabsTrigger>
      ))}
    </TabsList>
  )
}
